/**
 * Admin Identity & Organization Structure
 * 
 * Provides three industry-standard methods for org chart management:
 * 1. Manual Entry - Admin UI for adding/editing org units
 * 2. CSV Import - Bulk import from spreadsheets
 * 3. Directory Sync - SCIM 2.0 and LDAP integration
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  Shield,
  Building2,
  Users,
  Key,
  Plus,
  Settings,
  RefreshCw,
  Check,
  X,
  ChevronRight,
  Trash2,
  Edit2,
  Lock,
  Globe,
  AlertTriangle,
  Upload,
  Download,
  FileSpreadsheet,
  Server,
  Database,
  Eye,
  EyeOff,
  FolderTree,
} from 'lucide-react';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import {
  useEnterpriseIdentityStore,
  useIdentityProviders,
} from '../../../stores/prioritization/enterpriseIdentityStore';
import { SecondFactorRequirementPanel } from './identity/SecondFactorRequirementPanel';
import { ProvisioningPanel } from './identity/ProvisioningPanel';
import type {
  IdentityProviderType,
  IdentityProviderConfig,
  OrgUnit,
} from '../../../types/prioritization/enterpriseIdentity';
import { IDP_TEMPLATES } from '../../../types/prioritization/enterpriseIdentity';
import {
  parseOrgUnitCsv,
  csvRowsToOrgUnits,
  generateOrgUnitCsvTemplate,
  exportOrgUnitsToCsv,
  validateOrgUnitHierarchy,
  type CsvParseResult,
  type CsvOrgUnitRow,
  type LdapConfig,
  DEFAULT_AD_LDAP_CONFIG,
  DEFAULT_OPENLDAP_CONFIG,
} from '../../../lib/directorySync';

// ============================================================================
// Identity Provider Logos/Icons
// ============================================================================

const IDP_INFO: Record<IdentityProviderType, { name: string; color: string; description: string }> = {
  'okta': {
    name: 'Okta',
    color: 'bg-[var(--slate)]',
    description: 'Enterprise identity management with SAML/OIDC',
  },
  'azure-ad': {
    name: 'Azure Active Directory',
    color: 'bg-[var(--slate)]',
    description: 'Microsoft Entra ID for Microsoft 365 organizations',
  },
  'google-workspace': {
    name: 'Google Workspace',
    color: 'bg-[var(--green)]',
    description: 'Google Workspace directory integration',
  },
  'onelogin': {
    name: 'OneLogin',
    color: 'bg-[var(--slate)]',
    description: 'Cloud-based identity and access management',
  },
  'ping-identity': {
    name: 'Ping Identity',
    color: 'bg-[var(--coral)]',
    description: 'Enterprise identity security platform',
  },
  'saml-generic': {
    name: 'SAML 2.0',
    color: 'bg-[var(--surface)]',
    description: 'Generic SAML 2.0 identity provider',
  },
  'oidc-generic': {
    name: 'OpenID Connect',
    color: 'bg-[var(--mustard)]',
    description: 'Generic OIDC identity provider',
  },
  'local': {
    name: 'Local Authentication',
    color: 'bg-[var(--surface)]',
    description: 'Built-in email/password authentication',
  },
};

// ============================================================================
// Main Component
// ============================================================================

export default function AdminIdentity() {
  const [activeTab, setActiveTab] = useState<'import' | 'providers' | 'sync' | 'tenants'>('tenants');
  const canAny = useUserStore((state) => state.canAny);
  
  // Check admin permissions
  const hasAdminAccess = canAny(['admin.settings', 'admin.users']);
  
  if (!hasAdminAccess) {
    return (
      <div className="space-y-6">
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[var(--coral-dim)] rounded-full">
              <Lock className="h-6 w-6 text-[var(--coral)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--coral)] mb-2">Access Denied</h2>
              <p className="text-sm text-[var(--coral)]">
                You don't have permission to manage identity settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Key className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Identity & Directory"
        subtitle="Configure identity providers, directory sync, and bulk import for user management"
      />
      
      {/* Tabs */}
      <div className="border-b border-[var(--rule)]">
        <nav className="-mb-px flex space-x-8">
          <TabButton
            active={activeTab === 'tenants'}
            onClick={() => setActiveTab('tenants')}
            icon={Building2}
            label="Tenants & Workspaces"
          />
          <TabButton
            active={activeTab === 'providers'}
            onClick={() => setActiveTab('providers')}
            icon={Key}
            label="Identity Providers"
          />
          <TabButton
            active={activeTab === 'sync'}
            onClick={() => setActiveTab('sync')}
            icon={RefreshCw}
            label="Provisioning"
          />
          <TabButton
            active={activeTab === 'import'}
            onClick={() => setActiveTab('import')}
            icon={Upload}
            label="Import"
          />
        </nav>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'tenants' && <TenantsTab />}
      {activeTab === 'providers' && <IdentityProvidersTab />}
      {activeTab === 'sync' && <ProvisioningTab />}
      {activeTab === 'import' && <ImportTab />}
    </div>
  );
}

// ============================================================================
// Tenants & Workspaces Tab with Full CRUD
// ============================================================================

import { setStrictWorkspaceMode, getCurrentWorkspaceId } from '../../../lib/supabaseSync';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  description?: string;
  plan: string;
  status: string;
}

interface Workspace {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  description?: string;
  status: string;
  is_demo: boolean;
}

const TENANT_KEY = 'peakPriorities_currentTenantId';
const WORKSPACE_KEY = 'peakPriorities_demoWorkspaceId';
const DEMO_MODE_KEY = 'peakPriorities_demoMode';

const PLAN_OPTIONS = ['free', 'pro', 'enterprise'] as const;

function TenantsTab() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  
  // CRUD Modal State
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const supabase = getSupabaseClient();
      
      // Check if user is super admin
      const { data: superAdminData } = await supabase
        .from('super_admins')
        .select('user_id, can_access_all_workspaces')
        .limit(1);
      
      setIsSuperAdmin(superAdminData && superAdminData.length > 0);

      // Load all tenants
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('id, name, slug, description, plan, status')
        .order('name');

      if (tenantData) setTenants(tenantData);

      // Load all workspaces
      const { data: workspaceData } = await supabase
        .from('workspaces')
        .select('id, tenant_id, name, slug, description, status, is_demo')
        .order('name');

      if (workspaceData) setWorkspaces(workspaceData);

      // Get current selections from sessionStorage and validate
      const storedTenantId = sessionStorage.getItem(TENANT_KEY);
      const storedWorkspaceId = sessionStorage.getItem(WORKSPACE_KEY);
      
      let effectiveTenantId = storedTenantId;
      let effectiveWorkspaceId = storedWorkspaceId;
      
      // Validate: if workspace is set, derive tenant from it
      if (storedWorkspaceId && workspaceData) {
        const storedWorkspace = workspaceData.find(w => w.id === storedWorkspaceId);
        if (storedWorkspace) {
          effectiveTenantId = storedWorkspace.tenant_id;
          if (storedTenantId && storedTenantId !== storedWorkspace.tenant_id) {
            console.warn('[TenantsTab] Tenant-workspace mismatch, using workspace tenant');
            sessionStorage.setItem(TENANT_KEY, storedWorkspace.tenant_id);
          }
        } else {
          effectiveWorkspaceId = null;
          sessionStorage.removeItem(WORKSPACE_KEY);
        }
      }
      
      // If tenant is set but no workspace, select first workspace for that tenant
      if (effectiveTenantId && !effectiveWorkspaceId && workspaceData) {
        const tenantWorkspaces = workspaceData.filter(w => w.tenant_id === effectiveTenantId);
        if (tenantWorkspaces.length > 0) {
          effectiveWorkspaceId = tenantWorkspaces[0].id;
          sessionStorage.setItem(WORKSPACE_KEY, effectiveWorkspaceId);
          sessionStorage.setItem(DEMO_MODE_KEY, 'true');
        }
      }
      
      // Default to first tenant if nothing set
      if (!effectiveTenantId && tenantData && tenantData.length > 0) {
        effectiveTenantId = tenantData[0].id;
        const tenantWorkspaces = workspaceData?.filter(w => w.tenant_id === effectiveTenantId) || [];
        if (tenantWorkspaces.length > 0) {
          effectiveWorkspaceId = tenantWorkspaces[0].id;
        }
      }
      
      setCurrentTenantId(effectiveTenantId);
      setCurrentWorkspaceId(effectiveWorkspaceId);

    } catch (err) {
      console.error('[TenantsTab] Exception:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSwitchTenant = async (tenant: Tenant) => {
    setSwitching(true);
    
    const tenantWorkspaces = workspaces.filter(w => w.tenant_id === tenant.id);
    const defaultWorkspace = tenantWorkspaces[0];

    sessionStorage.setItem(TENANT_KEY, tenant.id);
    if (defaultWorkspace) {
      sessionStorage.setItem(WORKSPACE_KEY, defaultWorkspace.id);
      sessionStorage.setItem(DEMO_MODE_KEY, 'true');
      setStrictWorkspaceMode(!defaultWorkspace.is_demo);
    }

    setCurrentTenantId(tenant.id);
    setCurrentWorkspaceId(defaultWorkspace?.id || null);

    setTimeout(() => {
      window.location.href = '/prioritization';
    }, 500);
  };

  const handleSwitchWorkspace = async (workspace: Workspace) => {
    setSwitching(true);

    sessionStorage.setItem(WORKSPACE_KEY, workspace.id);
    sessionStorage.setItem(DEMO_MODE_KEY, 'true');
    if (workspace.tenant_id) {
      sessionStorage.setItem(TENANT_KEY, workspace.tenant_id);
    }

    setStrictWorkspaceMode(!workspace.is_demo);

    setCurrentTenantId(workspace.tenant_id);
    setCurrentWorkspaceId(workspace.id);

    setTimeout(() => {
      window.location.href = '/prioritization';
    }, 500);
  };

  // ========================================================================
  // Tenant CRUD Handlers
  // ========================================================================
  
  const handleCreateTenant = () => {
    setEditingTenant(null);
    setShowTenantModal(true);
    setError(null);
  };

  const handleEditTenant = (tenant: Tenant, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTenant(tenant);
    setShowTenantModal(true);
    setError(null);
  };

  const handleDeleteTenant = async (tenant: Tenant, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const tenantWorkspaces = workspaces.filter(w => w.tenant_id === tenant.id);
    if (tenantWorkspaces.length > 0) {
      alert(`Cannot delete tenant "${tenant.name}" - it has ${tenantWorkspaces.length} workspace(s). Delete the workspaces first.`);
      return;
    }
    
    if (!confirm(`Are you sure you want to delete tenant "${tenant.name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('tenants').delete().eq('id', tenant.id);
      if (error) throw error;
      await loadData();
    } catch (err) {
      alert(`Failed to delete tenant: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleSaveTenant = async (data: { name: string; slug: string; description: string; plan: string }) => {
    setSaving(true);
    setError(null);
    
    try {
      const supabase = getSupabaseClient();
      
      if (editingTenant) {
        // Update existing tenant
        const { error } = await supabase
          .from('tenants')
          .update({
            name: data.name,
            slug: data.slug,
            description: data.description || null,
            plan: data.plan,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingTenant.id);
        
        if (error) throw error;
      } else {
        // Create new tenant
        const { data: newTenant, error } = await supabase
          .from('tenants')
          .insert({
            name: data.name,
            slug: data.slug,
            description: data.description || null,
            plan: data.plan,
            status: 'active',
          })
          .select('id')
          .single();
        
        if (error) throw error;
        
        // Create a default workspace for the new tenant
        let defaultWorkspaceId: string | null = null;
        if (newTenant) {
          const { data: newWorkspace, error: wsError } = await supabase
            .from('workspaces')
            .insert({
              tenant_id: newTenant.id,
              name: `${data.name} Workspace`,
              slug: `${data.slug}-workspace`,
              description: `Default workspace for ${data.name}`,
              status: 'active',
              is_demo: false,
            })
            .select('id')
            .single();
          
          if (wsError) {
            console.error('[AdminIdentity] Error creating default workspace:', wsError);
          } else if (newWorkspace) {
            defaultWorkspaceId = newWorkspace.id;
          }
        }
        
        // Switch to the newly created tenant and workspace
        if (newTenant) {
          sessionStorage.setItem(TENANT_KEY, newTenant.id);
          if (defaultWorkspaceId) {
            sessionStorage.setItem(WORKSPACE_KEY, defaultWorkspaceId);
          }
        }
      }
      
      setShowTenantModal(false);
      setEditingTenant(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tenant');
    } finally {
      setSaving(false);
    }
  };

  // ========================================================================
  // Workspace CRUD Handlers
  // ========================================================================
  
  const handleCreateWorkspace = () => {
    if (!currentTenantId) {
      alert('Please select a tenant first');
      return;
    }
    setEditingWorkspace(null);
    setShowWorkspaceModal(true);
    setError(null);
  };

  const handleEditWorkspace = (workspace: Workspace, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingWorkspace(workspace);
    setShowWorkspaceModal(true);
    setError(null);
  };

  const handleDeleteWorkspace = async (workspace: Workspace, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (workspace.id === currentWorkspaceId) {
      alert('Cannot delete the currently active workspace. Switch to another workspace first.');
      return;
    }
    
    if (!confirm(`Are you sure you want to delete workspace "${workspace.name}"? All data in this workspace will be lost.`)) {
      return;
    }
    
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('workspaces').delete().eq('id', workspace.id);
      if (error) throw error;
      await loadData();
    } catch (err) {
      alert(`Failed to delete workspace: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleSaveWorkspace = async (data: { name: string; slug: string; description: string; is_demo: boolean }) => {
    setSaving(true);
    setError(null);
    
    try {
      const supabase = getSupabaseClient();
      
      if (editingWorkspace) {
        // Update existing workspace
        const { error } = await supabase
          .from('workspaces')
          .update({
            name: data.name,
            slug: data.slug,
            description: data.description || null,
            is_demo: data.is_demo,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingWorkspace.id);
        
        if (error) throw error;
      } else {
        // Create new workspace
        const { error } = await supabase
          .from('workspaces')
          .insert({
            tenant_id: currentTenantId,
            name: data.name,
            slug: data.slug,
            description: data.description || null,
            is_demo: data.is_demo,
            status: 'active',
          });
        
        if (error) throw error;
      }
      
      setShowWorkspaceModal(false);
      setEditingWorkspace(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save workspace');
    } finally {
      setSaving(false);
    }
  };

  const currentTenant = tenants.find(t => t.id === currentTenantId);
  const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId);
  const filteredWorkspaces = currentTenantId 
    ? workspaces.filter(w => w.tenant_id === currentTenantId)
    : workspaces;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[var(--text-ter)]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 mt-6">
      {/* Super Admin Badge */}
      {isSuperAdmin && (
        <div className="flex items-center gap-2 px-4 py-2 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <Shield className="h-5 w-5 text-[var(--text-ter)]" />
          <span className="text-sm font-medium text-[var(--text-sec)]">Super Administrator</span>
          <span className="text-sm text-[var(--text-sec)]">— You can manage all tenants and workspaces</span>
        </div>
      )}

      {/* Current Context */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Current Context</h2>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="text-sm font-medium text-[var(--text-ter)]">Tenant (Organization)</div>
            <div className="flex items-center gap-3 p-3 bg-[var(--surface)]">
              <Building2 className="h-5 w-5 text-[var(--text-sec)]" />
              <div>
                <div className="font-medium text-[var(--text-pri)]">{currentTenant?.name || 'None selected'}</div>
                <div className="text-xs text-[var(--text-ter)]">{currentTenant?.slug}</div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-[var(--text-ter)]">Workspace</div>
            <div className="flex items-center gap-3 p-3 bg-[var(--surface)]">
              <FolderTree className="h-5 w-5 text-[var(--text-sec)]" />
              <div>
                <div className="font-medium text-[var(--text-pri)]">{currentWorkspace?.name || 'None selected'}</div>
                <div className="text-xs text-[var(--text-ter)]">
                  {currentWorkspace?.is_demo ? 'Demo workspace' : 'Production workspace'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {currentTenant && (
        <SecondFactorRequirementPanel tenantId={currentTenant.id} tenantName={currentTenant.name} isPlatformAdmin={isSuperAdmin} />
      )}

      {/* Tenants */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Tenants</h2>
            <p className="text-sm text-[var(--text-ter)]">
              Each tenant is a separate organization with isolated data
            </p>
          </div>
          {isSuperAdmin && (
            <button
              onClick={handleCreateTenant}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Tenant
            </button>
          )}
        </div>
        
        {switching && (
          <div className="mb-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Switching context...
          </div>
        )}

        <div className="grid gap-3">
          {tenants.map((tenant) => {
            const isActive = tenant.id === currentTenantId;
            const tenantWorkspaces = workspaces.filter(w => w.tenant_id === tenant.id);
            
            return (
              <div
                key={tenant.id}
                onClick={() => handleSwitchTenant(tenant)}
                className={`w-full flex items-center gap-4 p-4  border text-left transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-primary-50 border-primary-200'
                    : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:bg-[var(--surface)]'
                } ${switching ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <div className={`p-2  ${isActive ? 'bg-primary-100' : 'bg-[var(--hover-tint)]'}`}>
                  <Building2 className={`h-6 w-6 ${isActive ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isActive ? 'text-primary-900' : 'text-[var(--text-pri)]'}`}>
                      {tenant.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      tenant.plan === 'enterprise' 
                        ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' 
                        : tenant.plan === 'pro'
                        ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                        : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                    }`}>
                      {tenant.plan}
                    </span>
                  </div>
                  {tenant.description && (
                    <div className="text-sm text-[var(--text-ter)] truncate">{tenant.description}</div>
                  )}
                  <div className="text-xs text-[var(--text-ter)] mt-1">
                    {tenantWorkspaces.length} workspace{tenantWorkspaces.length !== 1 ? 's' : ''}
                  </div>
                </div>
                {isActive && <Check className="h-5 w-5 text-primary-600" />}
                
                {/* Admin Actions */}
                {isSuperAdmin && (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleEditTenant(tenant, e)}
                      className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] rounded"
                      title="Edit Tenant"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteTenant(tenant, e)}
                      className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
                      title="Delete Tenant"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <ChevronRight className="h-5 w-5 text-[var(--text-ter)]" />
              </div>
            );
          })}
          
          {tenants.length === 0 && (
            <div className="text-center py-8 text-[var(--text-ter)]">
              No tenants found. {isSuperAdmin && 'Create one to get started.'}
            </div>
          )}
        </div>
      </div>

      {/* Workspaces */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Workspaces {currentTenant && <span className="font-normal text-[var(--text-ter)]">in {currentTenant.name}</span>}
            </h2>
            <p className="text-sm text-[var(--text-ter)]">
              Each workspace contains its own candidates, cycles, and candidate pools
            </p>
          </div>
          {(isSuperAdmin || currentTenantId) && (
            <button
              onClick={handleCreateWorkspace}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Workspace
            </button>
          )}
        </div>

        <div className="grid gap-3">
          {filteredWorkspaces.map((workspace) => {
            const isActive = workspace.id === currentWorkspaceId;
            
            return (
              <div
                key={workspace.id}
                onClick={() => handleSwitchWorkspace(workspace)}
                className={`w-full flex items-center gap-4 p-4  border text-left transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-primary-50 border-primary-200'
                    : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:bg-[var(--surface)]'
                } ${switching ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <div className={`p-2  ${isActive ? 'bg-primary-100' : 'bg-[var(--hover-tint)]'}`}>
                  <FolderTree className={`h-6 w-6 ${isActive ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isActive ? 'text-primary-900' : 'text-[var(--text-pri)]'}`}>
                      {workspace.name}
                    </span>
                    {workspace.is_demo ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)]">
                        Demo
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                        Production
                      </span>
                    )}
                  </div>
                  {workspace.description && (
                    <div className="text-sm text-[var(--text-ter)] truncate">{workspace.description}</div>
                  )}
                </div>
                {isActive && <Check className="h-5 w-5 text-primary-600" />}
                
                {/* Admin Actions */}
                {(isSuperAdmin || currentTenantId === workspace.tenant_id) && (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleEditWorkspace(workspace, e)}
                      className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] rounded"
                      title="Edit Workspace"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteWorkspace(workspace, e)}
                      className={`p-2 rounded ${
                        workspace.id === currentWorkspaceId
                          ? 'text-[var(--text-ter)] cursor-not-allowed'
                          : 'text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)]'
                      }`}
                      title={workspace.id === currentWorkspaceId ? 'Cannot delete active workspace' : 'Delete Workspace'}
                      disabled={workspace.id === currentWorkspaceId}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {filteredWorkspaces.length === 0 && (
            <div className="text-center py-8 text-[var(--text-ter)]">
              No workspaces found for this tenant. Create one to get started.
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
        <h3 className="text-sm font-medium text-[var(--text-sec)]">About Multi-Tenancy</h3>
        <p className="mt-1 text-sm text-[var(--text-sec)]">
          <strong>Tenants</strong> are separate organizations with completely isolated data. 
          <strong> Workspaces</strong> are subdivisions within a tenant for organizing work. 
          As a super administrator, you can manage all tenants and workspaces.
        </p>
      </div>

      {/* Tenant Modal */}
      {showTenantModal && (
        <TenantModal
          tenant={editingTenant}
          saving={saving}
          error={error}
          onSave={handleSaveTenant}
          onClose={() => { setShowTenantModal(false); setEditingTenant(null); setError(null); }}
        />
      )}

      {/* Workspace Modal */}
      {showWorkspaceModal && (
        <WorkspaceModal
          workspace={editingWorkspace}
          saving={saving}
          error={error}
          onSave={handleSaveWorkspace}
          onClose={() => { setShowWorkspaceModal(false); setEditingWorkspace(null); setError(null); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Tenant Modal
// ============================================================================

function TenantModal({
  tenant,
  saving,
  error,
  onSave,
  onClose,
}: {
  tenant: Tenant | null;
  saving: boolean;
  error: string | null;
  onSave: (data: { name: string; slug: string; description: string; plan: string }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(tenant?.name || '');
  const [slug, setSlug] = useState(tenant?.slug || '');
  const [description, setDescription] = useState(tenant?.description || '');
  const [plan, setPlan] = useState(tenant?.plan || 'pro');
  const [autoSlug, setAutoSlug] = useState(!tenant);

  // Auto-generate slug from name
  useEffect(() => {
    if (autoSlug && name) {
      setSlug(name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    }
  }, [name, autoSlug]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;
    onSave({ name: name.trim(), slug: slug.trim(), description: description.trim(), plan });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            {tenant ? 'Edit Tenant' : 'Create Tenant'}
          </h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Tenant Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Acme Corporation"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Slug *
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={slug}
                onChange={(e) => { setSlug(e.target.value); setAutoSlug(false); }}
                className="flex-1 px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="acme-corporation"
                required
              />
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-1">Used in URLs and API calls</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
              placeholder="A brief description of this organization"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Plan
            </label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)]"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              disabled={saving || !name.trim() || !slug.trim()}
            >
              {saving ? 'Saving...' : tenant ? 'Update Tenant' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Workspace Modal
// ============================================================================

function WorkspaceModal({
  workspace,
  saving,
  error,
  onSave,
  onClose,
}: {
  workspace: Workspace | null;
  saving: boolean;
  error: string | null;
  onSave: (data: { name: string; slug: string; description: string; is_demo: boolean }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(workspace?.name || '');
  const [slug, setSlug] = useState(workspace?.slug || '');
  const [description, setDescription] = useState(workspace?.description || '');
  const [isDemo, setIsDemo] = useState(workspace?.is_demo ?? false);
  const [autoSlug, setAutoSlug] = useState(!workspace);

  // Auto-generate slug from name
  useEffect(() => {
    if (autoSlug && name) {
      setSlug(name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    }
  }, [name, autoSlug]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;
    onSave({ name: name.trim(), slug: slug.trim(), description: description.trim(), is_demo: isDemo });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            {workspace ? 'Edit Workspace' : 'Create Workspace'}
          </h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Workspace Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Q1 2026 Planning"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Slug *
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setAutoSlug(false); }}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="q1-2026-planning"
              required
            />
            <p className="text-xs text-[var(--text-ter)] mt-1">Used in URLs and API calls</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
              placeholder="A brief description of this workspace"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isDemo"
              checked={isDemo}
              onChange={(e) => setIsDemo(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-[var(--rule)] rounded focus:ring-primary-500"
            />
            <label htmlFor="isDemo" className="text-sm text-[var(--text-sec)]">
              Demo Workspace
              <span className="text-xs text-[var(--text-ter)] ml-2">
                (Demo workspaces show sample data and have relaxed filtering)
              </span>
            </label>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)]"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              disabled={saving || !name.trim() || !slug.trim()}
            >
              {saving ? 'Saving...' : workspace ? 'Update Workspace' : 'Create Workspace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Tab Button
// ============================================================================

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
        active
          ? 'border-primary-500 text-primary-600'
          : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:border-[var(--rule)]'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ============================================================================
// Import Tab (CSV, SCIM Preview, LDAP Preview)
// ============================================================================

function ImportTab() {
  const [importMethod, setImportMethod] = useState<'csv' | 'ldap'>('csv');
  
  return (
    <div className="space-y-6">
      {/* Method Selection */}
      <p className="text-sm text-[var(--text-sec)]">
        To have Okta or Microsoft Entra ID add and remove people as they change, use the Provisioning tab (SCIM).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ImportMethodCard
          selected={importMethod === 'csv'}
          onClick={() => setImportMethod('csv')}
          icon={FileSpreadsheet}
          title="CSV Import"
          description="Upload a spreadsheet with your org structure"
          badge="Manual"
        />
        <ImportMethodCard
          selected={importMethod === 'ldap'}
          onClick={() => setImportMethod('ldap')}
          icon={Server}
          title="LDAP / Active Directory"
          description="Connect to on-premises directory"
          badge="On-Prem"
        />
      </div>
      
      {/* Method Content */}
      {importMethod === 'csv' && <CsvImportSection />}
      {importMethod === 'ldap' && <LdapConfigSection />}
    </div>
  );
}

function ImportMethodCard({
  selected,
  onClick,
  icon: Icon,
  title,
  description,
  badge,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ElementType;
  title: string;
  description: string;
  badge: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`p-4 border-2  text-left transition-all ${
        selected
          ? 'border-primary-500 bg-primary-50'
          : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <Icon className={`h-6 w-6 ${selected ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          selected ? 'bg-primary-100 text-primary-700' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
        }`}>
          {badge}
        </span>
      </div>
      <h3 className={`font-medium ${selected ? 'text-primary-900' : 'text-[var(--text-pri)]'}`}>
        {title}
      </h3>
      <p className="text-sm text-[var(--text-ter)] mt-1">{description}</p>
    </button>
  );
}

// ============================================================================
// CSV Import Section
// ============================================================================

function CsvImportSection() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Select raw state to avoid infinite loops
  const orgUnitsMap = useEnterpriseIdentityStore((state) => state.orgUnits);
  const addOrgUnit = useEnterpriseIdentityStore((state) => state.addOrgUnit);
  
  // Derive arrays from raw Map state using useMemo
  const orgUnits = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  
  const [, setCsvContent] = useState('');
  const [parseResult, setParseResult] = useState<CsvParseResult<CsvOrgUnitRow> | null>(null);
  const [previewUnits, setPreviewUnits] = useState<OrgUnit[]>([]);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setCsvContent(text);
      handleParse(text);
    };
    reader.readAsText(file);
  };
  
  const handleParse = (text: string) => {
    const result = parseOrgUnitCsv(text);
    setParseResult(result);
    
    if (result.success && result.data.length > 0) {
      const { units } = csvRowsToOrgUnits(result.data, orgUnitsMap);
      setPreviewUnits(units);
      
      // Validate hierarchy
      const validationErrors = validateOrgUnitHierarchy(units);
      if (validationErrors.length > 0) {
        setParseResult(prev => prev ? {
          ...prev,
          warnings: [...prev.warnings, ...validationErrors],
        } : null);
      }
    } else {
      setPreviewUnits([]);
    }
    
    setImportComplete(false);
  };
  
  const handleImport = async () => {
    if (previewUnits.length === 0) return;
    
    setImporting(true);
    
    // Import units in order (parents first)
    const sortedUnits = [...previewUnits].sort((a, b) => a.depth - b.depth);
    
    for (const unit of sortedUnits) {
      addOrgUnit({
        name: unit.name,
        displayName: unit.displayName,
        type: unit.type,
        parentId: unit.parentId,
        path: unit.path,
        depth: unit.depth,
        description: unit.description,
        costCenter: unit.costCenter,
        location: unit.location,
        active: unit.active,
      });
    }
    
    setImporting(false);
    setImportComplete(true);
    setCsvContent('');
    setParseResult(null);
    setPreviewUnits([]);
  };
  
  const handleDownloadTemplate = () => {
    const template = generateOrgUnitCsvTemplate();
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'org-units-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleExport = () => {
    const csv = exportOrgUnitsToCsv(orgUnits);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `org-units-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="h-5 w-5 text-[var(--text-ter)] mt-0.5" />
          <div>
            <h3 className="font-medium text-[var(--text-sec)]">CSV Import</h3>
            <p className="text-sm text-[var(--text-sec)] mt-1">
              Import organizational units from a CSV file. Download the template to see the expected format.
              Supports hierarchy via parent references.
            </p>
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn btn-primary flex items-center gap-2"
        >
          <Upload className="h-4 w-4" />
          Upload CSV
        </button>
        <button
          onClick={handleDownloadTemplate}
          className="btn btn-secondary flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Download Template
        </button>
        {orgUnits.length > 0 && (
          <button
            onClick={handleExport}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export Current
          </button>
        )}
      </div>
      
      {/* Parse Results */}
      {parseResult && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="card">
            <h3 className="font-semibold text-[var(--text-pri)] mb-3">Parse Results</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-[var(--surface)]">
                <div className="text-2xl font-bold text-[var(--text-pri)]">{parseResult.stats.totalRows}</div>
                <div className="text-sm text-[var(--text-ter)]">Total Rows</div>
              </div>
              <div className="p-3 bg-[var(--green-dim)]">
                <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{parseResult.stats.validRows}</div>
                <div className="text-sm text-[var(--text-ter)]">Valid</div>
              </div>
              <div className="p-3 bg-[var(--coral-dim)]">
                <div className="text-2xl font-bold text-[var(--coral)]">{parseResult.stats.skippedRows}</div>
                <div className="text-sm text-[var(--text-ter)]">Skipped</div>
              </div>
            </div>
          </div>
          
          {/* Errors */}
          {parseResult.errors.length > 0 && (
            <div className="card border-[var(--coral)] bg-[var(--coral-dim)]">
              <h3 className="font-semibold text-[var(--coral)] mb-2 flex items-center gap-2">
                <X className="h-4 w-4" />
                Errors ({parseResult.errors.length})
              </h3>
              <ul className="text-sm text-[var(--coral)] space-y-1">
                {parseResult.errors.slice(0, 10).map((error, i) => (
                  <li key={i}>
                    Row {error.row}: {error.message}
                    {error.column && ` (column: ${error.column})`}
                  </li>
                ))}
                {parseResult.errors.length > 10 && (
                  <li className="text-[var(--coral)]">... and {parseResult.errors.length - 10} more errors</li>
                )}
              </ul>
            </div>
          )}
          
          {/* Warnings */}
          {parseResult.warnings.length > 0 && (
            <div className="card border-[var(--mustard)] bg-[var(--mustard-dim)]">
              <h3 className="font-semibold text-[var(--mustard)] mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Warnings ({parseResult.warnings.length})
              </h3>
              <ul className="text-sm text-[var(--mustard)] space-y-1">
                {parseResult.warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Preview */}
          {previewUnits.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[var(--text-pri)]">Preview ({previewUnits.length} units)</h3>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Import All
                    </>
                  )}
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto border border-[var(--rule)]">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--surface)] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-[var(--text-sec)]">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-[var(--text-sec)]">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-[var(--text-sec)]">Parent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--rule)]">
                    {previewUnits.map((unit, i) => (
                      <tr key={i} className="hover:bg-[var(--surface)]">
                        <td className="px-3 py-2">
                          <span style={{ paddingLeft: `${unit.depth * 12}px` }}>
                            {unit.displayName}
                          </span>
                        </td>
                        <td className="px-3 py-2 capitalize">{unit.type}</td>
                        <td className="px-3 py-2 text-[var(--text-ter)]">
                          {unit.parentId ? previewUnits.find(u => u.id === unit.parentId)?.displayName || unit.parentId : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Import Success */}
      {importComplete && (
        <div className="card border-[var(--green)] bg-[var(--green-dim)]">
          <div className="flex items-center gap-3">
            <Check className="h-5 w-5 text-[var(--green)]" />
            <div>
              <h3 className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Import Complete</h3>
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                Organizational units have been imported successfully.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LDAP Configuration Section
// ============================================================================

function LdapConfigSection() {
  const [ldapType, setLdapType] = useState<'ad' | 'openldap' | 'custom'>('ad');
  const [config, setConfig] = useState<Partial<LdapConfig>>(DEFAULT_AD_LDAP_CONFIG);
  const [showPassword, setShowPassword] = useState(false);
  
  const handleTypeChange = (type: 'ad' | 'openldap' | 'custom') => {
    setLdapType(type);
    if (type === 'ad') {
      setConfig(DEFAULT_AD_LDAP_CONFIG);
    } else if (type === 'openldap') {
      setConfig(DEFAULT_OPENLDAP_CONFIG);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
        <div className="flex items-start gap-3">
          <Server className="h-5 w-5 text-[var(--mustard)] mt-0.5" />
          <div>
            <h3 className="font-medium text-[var(--mustard)]">LDAP / Active Directory</h3>
            <p className="text-sm text-[var(--mustard)] mt-1">
              Connect to on-premises directory services to sync organizational units and users.
              Supports Microsoft Active Directory and OpenLDAP.
            </p>
          </div>
        </div>
      </div>
      
      {/* Directory Type Selection */}
      <div className="card">
        <h3 className="font-semibold text-[var(--text-pri)] mb-4">Directory Type</h3>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => handleTypeChange('ad')}
            className={`p-4 border-2  text-left transition-all ${
              ldapType === 'ad'
                ? 'border-primary-500 bg-primary-50'
                : 'border-[var(--rule)] hover:border-[var(--rule)]'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Database className="h-5 w-5 text-[var(--text-ter)]" />
              <span className="font-medium">Active Directory</span>
            </div>
            <p className="text-xs text-[var(--text-ter)]">Microsoft AD / Entra ID</p>
          </button>
          
          <button
            onClick={() => handleTypeChange('openldap')}
            className={`p-4 border-2  text-left transition-all ${
              ldapType === 'openldap'
                ? 'border-primary-500 bg-primary-50'
                : 'border-[var(--rule)] hover:border-[var(--rule)]'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Database className="h-5 w-5 text-[var(--green)]" />
              <span className="font-medium">OpenLDAP</span>
            </div>
            <p className="text-xs text-[var(--text-ter)]">Standard LDAP directory</p>
          </button>
          
          <button
            onClick={() => handleTypeChange('custom')}
            className={`p-4 border-2  text-left transition-all ${
              ldapType === 'custom'
                ? 'border-primary-500 bg-primary-50'
                : 'border-[var(--rule)] hover:border-[var(--rule)]'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Settings className="h-5 w-5 text-[var(--text-sec)]" />
              <span className="font-medium">Custom</span>
            </div>
            <p className="text-xs text-[var(--text-ter)]">Manual configuration</p>
          </button>
        </div>
      </div>
      
      {/* Connection Settings */}
      <div className="card">
        <h3 className="font-semibold text-[var(--text-pri)] mb-4">Connection Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Server URL <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={config.url || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, url: e.target.value }))}
              placeholder="ldap://ad.company.com:389 or ldaps://..."
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Base DN <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={config.baseDn || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, baseDn: e.target.value }))}
              placeholder="dc=company,dc=com"
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Bind DN <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={config.bindDn || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, bindDn: e.target.value }))}
                placeholder="cn=admin,dc=company,dc=com"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Bind Password <span className="text-[var(--coral)]">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={config.bindCredential || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, bindCredential: e.target.value }))}
                  placeholder="••••••••"
                  className="flex-1 px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.useTls}
                onChange={(e) => setConfig(prev => ({ ...prev, useTls: e.target.checked }))}
                className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-[var(--text-sec)]">Use TLS/SSL</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.validateCertificate}
                onChange={(e) => setConfig(prev => ({ ...prev, validateCertificate: e.target.checked }))}
                className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-[var(--text-sec)]">Validate Certificate</span>
            </label>
          </div>
        </div>
      </div>
      
      {/* Search Settings */}
      <div className="card">
        <h3 className="font-semibold text-[var(--text-pri)] mb-4">Search Settings</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                User Search Base
              </label>
              <input
                type="text"
                value={config.userSearchBase || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, userSearchBase: e.target.value }))}
                placeholder="ou=Users,dc=company,dc=com"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                User Search Filter
              </label>
              <input
                type="text"
                value={config.userSearchFilter || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, userSearchFilter: e.target.value }))}
                placeholder="(objectClass=person)"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Group/OU Search Base
              </label>
              <input
                type="text"
                value={config.ouSearchBase || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, ouSearchBase: e.target.value }))}
                placeholder="dc=company,dc=com"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                OU Search Filter
              </label>
              <input
                type="text"
                value={config.ouSearchFilter || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, ouSearchFilter: e.target.value }))}
                placeholder="(objectClass=organizationalUnit)"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Sync Options */}
      <div className="card">
        <h3 className="font-semibold text-[var(--text-pri)] mb-4">Sync Options</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.ousAsOrgUnits}
              onChange={(e) => setConfig(prev => ({ ...prev, ousAsOrgUnits: e.target.checked }))}
              className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-[var(--text-sec)]">Import Organizational Units (OUs) from directory</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.groupsAsOrgUnits}
              onChange={(e) => setConfig(prev => ({ ...prev, groupsAsOrgUnits: e.target.checked }))}
              className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-[var(--text-sec)]">Import security groups as org units</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.autoCreateUsers}
              onChange={(e) => setConfig(prev => ({ ...prev, autoCreateUsers: e.target.checked }))}
              className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-[var(--text-sec)]">Auto-create users on first sync</span>
          </label>
          
          <div className="pt-2">
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Sync Interval (minutes)
            </label>
            <input
              type="number"
              min="15"
              max="1440"
              value={config.syncInterval || 60}
              onChange={(e) => setConfig(prev => ({ ...prev, syncInterval: parseInt(e.target.value) }))}
              className="w-32 px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
      
      {/* No LDAP endpoint exists yet (nothing in supabase/functions or apps/api binds to a
          directory), so there is no connection to test and nowhere to save this form. The
          earlier button waited two seconds and reported success whenever three fields were
          filled in. */}
      <p className="text-sm text-[var(--text-ter)]">
        Testing and saving an LDAP connection are not available yet. These fields show what the
        import will ask for; nothing entered here is kept.
      </p>
    </div>
  );
}

// ============================================================================
// Identity Providers Tab
// ============================================================================

function IdentityProvidersTab() {
  const [showAddModal, setShowAddModal] = useState(false);
  const providers: IdentityProviderConfig[] = useIdentityProviders();
  const addProvider = useEnterpriseIdentityStore((state) => state.addIdentityProvider);
  const removeProvider = useEnterpriseIdentityStore((state) => state.removeIdentityProvider);
  
  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
        <div className="flex items-start gap-3">
          <Globe className="h-5 w-5 text-[var(--text-ter)] mt-0.5" />
          <div>
            <h3 className="font-medium text-[var(--text-sec)]">Enterprise Single Sign-On</h3>
            <p className="text-sm text-[var(--text-sec)] mt-1">
              Connect your identity provider to enable SSO and automatic user provisioning.
              Supported providers: Okta, Azure AD, Google Workspace, and generic SAML/OIDC.
            </p>
          </div>
        </div>
      </div>
      
      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {providers.map((provider) => {
          const info = IDP_INFO[provider.type];
          return (
            <div
              key={provider.id}
              className="card hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 ${info.color}  flex items-center justify-center`}>
                    <Key className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[var(--text-pri)]">{provider.name}</h3>
                      {provider.enabled ? (
                        <span className="px-2 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs rounded-full">
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[var(--text-sec)] mt-1">{info.name}</p>
                    {provider.domain && (
                      <p className="text-xs text-[var(--text-ter)] mt-1">
                        Domain: {provider.domain}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => removeProvider(provider.id)}
                    className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {provider.groupSync.enabled && (
                <div className="mt-3 pt-3 border-t border-[var(--rule)]">
                  <div className="flex items-center gap-2 text-xs text-[var(--text-ter)]">
                    <Users className="h-3.5 w-3.5" />
                    <span>Group sync enabled • {provider.groupSync.syncInterval || 60}min interval</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Add Provider Button */}
      <button
        onClick={() => setShowAddModal(true)}
        className="w-full p-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="h-5 w-5" />
        Add Identity Provider
      </button>
      
      {/* Add Provider Modal */}
      {showAddModal && (
        <AddProviderModal
          onClose={() => setShowAddModal(false)}
          onAdd={(type, name, domain) => {
            const template = IDP_TEMPLATES[type];
            addProvider({
              name,
              type,
              enabled: false,
              domain,
              ...template,
              attributeMapping: template.attributeMapping || {
                userId: 'sub',
                email: 'email',
                displayName: 'name',
              },
              groupSync: template.groupSync || {
                enabled: false,
                autoCreateGroups: false,
                autoAssignRoles: false,
              },
              defaultRoleMappings: [],
            });
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Add Provider Modal
// ============================================================================

function AddProviderModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (type: IdentityProviderType, name: string, domain: string) => void;
}) {
  const [selectedType, setSelectedType] = useState<IdentityProviderType | null>(null);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  
  const providerTypes = Object.entries(IDP_INFO).filter(([type]) => type !== 'local');
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-raised)] shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[var(--rule)]">
          <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add Identity Provider</h2>
          <p className="text-sm text-[var(--text-sec)] mt-1">
            Choose your identity provider type and configure the connection
          </p>
        </div>
        
        {!selectedType ? (
          <div className="p-6">
            <div className="grid grid-cols-2 gap-3">
              {providerTypes.map(([type, info]) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type as IdentityProviderType)}
                  className="p-4 border-2 border-[var(--rule)] hover:border-primary-400 hover:bg-primary-50 transition-all text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${info.color}  flex items-center justify-center`}>
                      <Key className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-[var(--text-pri)]">{info.name}</p>
                      <p className="text-xs text-[var(--text-ter)]">{info.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 p-3 bg-[var(--surface)]">
              <div className={`w-10 h-10 ${IDP_INFO[selectedType].color}  flex items-center justify-center`}>
                <Key className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-[var(--text-pri)]">{IDP_INFO[selectedType].name}</p>
                <button
                  onClick={() => setSelectedType(null)}
                  className="text-xs text-primary-600 hover:underline"
                >
                  Change provider
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Acme Corp SSO"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                {selectedType === 'okta' ? 'Okta Domain' : 
                 selectedType === 'azure-ad' ? 'Tenant ID' : 
                 'Domain / Issuer URL'}
              </label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder={
                  selectedType === 'okta' ? 'company.okta.com' :
                  selectedType === 'azure-ad' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' :
                  'https://...'
                }
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-[var(--mustard)] mt-0.5" />
                <div className="text-sm text-[var(--mustard)]">
                  <p className="font-medium">Additional configuration required</p>
                  <p className="text-xs mt-1">
                    After creating the provider, you'll need to configure client credentials,
                    certificates, and attribute mappings in the provider settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="p-6 border-t border-[var(--rule)] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            Cancel
          </button>
          {selectedType && (
            <button
              onClick={() => onAdd(selectedType, name || IDP_INFO[selectedType].name, domain)}
              disabled={!name.trim()}
              className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Provider
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Directory Sync Tab
// ============================================================================

function ProvisioningTab() {
  const [workspace, setWorkspace] = useState<{ id: string; name: string; tenantId: string | null } | null | undefined>(undefined);

  useEffect(() => {
    const id = getCurrentWorkspaceId();
    if (!id) {
      setWorkspace(null);
      return;
    }
    void getSupabaseClient()
      .from('workspaces')
      .select('id, name, tenant_id')
      .eq('id', id)
      .maybeSingle()
      .then(({ data }) => setWorkspace(data ? { id: data.id, name: data.name, tenantId: data.tenant_id } : null));
  }, []);

  if (workspace === undefined) return null;
  if (!workspace) {
    return <p className="mt-6 text-sm text-[var(--text-ter)]">Choose a workspace on the Tenants &amp; Workspaces tab first; provisioning is set per workspace.</p>;
  }
  return <ProvisioningPanel workspaceId={workspace.id} workspaceName={workspace.name} tenantId={workspace.tenantId} />;
}
